/**
 * Разбор и сборка таблиц для массовой загрузки учеников.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНЫМ ФАЙЛОМ. Заказчик грузит по 400 человек за раз, и ошибка
 * здесь стоит дорого: неверно разобранная строка — это либо не заведённый
 * ученик, либо заведённый с чужим адресом. Такое надо проверять тестами, а не
 * глазами, поэтому разбор живёт отдельно от окна.
 */

/**
 * Превратить содержимое CSV в список «Имя, почта» — тот вид, который уже
 * понимает сервер.
 *
 * ⚠️ ЗАГОЛОВОК ТАБЛИЦЫ ОТБРАСЫВАЕТСЯ. Люди выгружают из Excel вместе со
 * строкой «Имя, Почта», и без этого первым «учеником» заводился бы человек по
 * имени «Имя» с адресом «Почта».
 *
 * ⚠️ РАЗДЕЛИТЕЛЬ ОПРЕДЕЛЯЕТСЯ САМ. Excel в русской раскладке сохраняет CSV
 * через ТОЧКУ С ЗАПЯТОЙ, а не запятую. Требовать от школы менять настройки
 * Excel — верный способ получить «файл не читается».
 *
 * Кавычки снимаются: `"Иванов, Иван";mail@x.ru` — обычная выгрузка.
 */
export function csvToList(raw: string): string {
  const lines = raw.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== '');
  if (lines.length === 0) return '';

  const sep = pickSeparator(lines[0]);
  const rows = lines.map((l) => splitRow(l, sep));

  // Заголовок: в первой строке нет ничего похожего на почту.
  if (rows.length > 1 && !rows[0].some((c) => c.includes('@'))) rows.shift();

  return rows
    .map((cells) => {
      const email = cells.find((c) => c.includes('@')) ?? '';
      const name = cells.filter((c) => c !== email && c.trim() !== '')[0] ?? '';
      return email ? `${name}, ${email}`.replace(/^, /, '') : cells.join(' ');
    })
    .join('\n');
}

/** Какой разделитель в строке. Побеждает тот, которого больше. */
function pickSeparator(line: string): string {
  const counts: [string, number][] = [
    [';', (line.match(/;/g) ?? []).length],
    ['\t', (line.match(/\t/g) ?? []).length],
    [',', (line.match(/,/g) ?? []).length],
  ];
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 0 ? counts[0][0] : ',';
}

/** Разбить строку, уважая кавычки: `"Иванов, Иван",mail@x.ru` — две ячейки. */
function splitRow(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      // Две кавычки подряд внутри поля — это одна кавычка в тексте.
      if (quoted && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else quoted = !quoted;
      continue;
    }
    if (ch === sep && !quoted) {
      out.push(cur.trim());
      cur = '';
      continue;
    }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

/** Строка выгрузки: тот же список, что загрузили, плюс третий столбец. */
export type ExportRow = { full_name: string; email: string; password: string };

/**
 * Собрать CSV с паролями — ту же таблицу с третьим столбцом.
 *
 * ⚠️ РАЗДЕЛИТЕЛЬ — ТОЧКА С ЗАПЯТОЙ. Русский Excel открывает файл с запятыми
 * одним столбцом, и получается каша вместо таблицы.
 *
 * ⚠️ В НАЧАЛЕ СТОИТ МЕТКА КОДИРОВКИ (BOM). Без неё Excel читает кириллицу как
 * «ÐÐ¹Ð³ÐµÑ€Ð¸Ð¼» — файл вроде открылся, а имена нечитаемы.
 */
export function passwordsToCsv(rows: ExportRow[]): string {
  const esc = (v: string) => (/[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const head = ['Имя', 'Почта', 'Пароль'].join(';');
  const body = rows.map((r) =>
    [esc(r.full_name), esc(r.email), esc(passwordCell(r.password))].join(';'),
  );
  return '﻿' + [head, ...body].join('\n');
}

/**
 * Пароль в ячейке — с подписью перед цифрами.
 *
 * 🔴 БЕЗ ПОДПИСИ EXCEL СЪЕДАЕТ ВЕДУЩИЕ НУЛИ. Пароль «021447» он считает числом
 * 21447 и показывает на цифру короче — человек диктует его ученику, и вход не
 * работает. Поймано на настоящей выгрузке 05.09.2026: из 348 паролей 37
 * начинались с нуля, то есть у каждого девятого пароль в таблице был испорчен.
 *
 * ⚠️ ПОЧЕМУ ИМЕННО ПОДПИСЬ, А НЕ КАВЫЧКИ. Кавычки Excel снимает и всё равно
 * приводит к числу. Приём с `="021447"` работает, но ломает открытие в Google
 * Таблицах и в «Числах». Текст перед цифрами понятен человеку и не превращается
 * в число нигде — решение заказчика.
 */
function passwordCell(password: string): string {
  const clean = password.trim();
  return clean ? `Пароль: ${clean}` : '';
}
